import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useReturnTo } from '../lib/returnNav'
import { BackLink } from '../components/shared'
import {
  Container,
  VStack,
  HStack,
  Box,
  Heading,
  Text,
  Button,
  Badge,
  SimpleGrid,
  Spinner,
  Alert,
  Icon,
  useDisclosure,
  Tabs,
  SegmentGroup,
} from '@chakra-ui/react';
import { toaster } from '@/components/ui/toaster'
import {
  FiEdit2,
  FiRefreshCw,
  FiBarChart2,
  FiFileText,
  FiTag,
  FiBookOpen,
  FiSettings,
  FiUsers,
} from 'react-icons/fi'
import {
  useCampaignDetail,
  useCampaignStats,
  useUpdateCampaign,
  useRefreshCampaignLists,
} from '../hooks/useCampaign'
import { useIsMobile } from '../hooks/useIsMobile'
import { useDemoMode } from '../hooks/useClientConfig'
import { CampaignFormModal } from '../components/campaign/CampaignFormModal'
import { CampaignScriptCard } from '../components/campaign/CampaignScriptCard'
import {
  DraftSetupView,
  CampaignLifetimeStats,
  TodayPanel,
  PlanningSection,
  ToolCard,
  SimulationPanel,
  UnionSignalsCard,
} from '../components/campaign/dashboard'
import { UnionOnly } from '../components/scoping/UnionOnly'
import { canManuallyRefreshCampaign } from '../lib/listRefresh'
import { extractApiMessage } from '../lib/apiError'
import {
  useCampaignViewSettings,
  useSaveCampaignViewSettings,
  useFollowUpSummary,
  useFollowUpKinds,
} from '../hooks/useFollowUp'
import { FollowUpTab } from '../components/campaign/followUp/FollowUpTab'
import { mergeSectionSlots } from '../components/campaign/followUp/sectionSlots'
import { ChaseSection } from '../components/campaign/followUp/chase/ChaseSection'
import { useFollowUpRouteSlots } from '../hooks/useFollowUpRouteSlots'
import { buildCallSectionSlots } from '../components/campaign/followUp/calls/callSectionSlots'
import { buildCallRowActions } from '../components/campaign/followUp/calls/callRowActions'
import type { CampaignDisplayMode } from '../types/followUp'

// CF-1 P1: Canvass | Follow-up tabs, URL-synced (?tab=), same pattern as LobbyDayEventPage.
const CAMPAIGN_TABS = ['canvass', 'follow-up'] as const
type CampaignTab = typeof CAMPAIGN_TABS[number]

const VIEW_OPTIONS: { value: CampaignDisplayMode; label: string }[] = [
  { value: 'Standard', label: 'Standard' },
  { value: 'FinalPush', label: 'Final push' },
]

const statusBadgeColor: Record<string, string> = {
  Draft: 'gray',
  Active: 'green',
  Completed: 'purple',
  Archived: 'orange',
}

export function CampaignDashboardPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const back = useReturnTo({ to: '/dashboard/campaigns', label: 'Back to Campaigns' })

  const { data: campaign, isLoading, error } = useCampaignDetail(id)
  const { data: stats } = useCampaignStats(id)
  const updateMutation = useUpdateCampaign(id ?? '')
  const refreshMutation = useRefreshCampaignLists(id ?? '')

  const editModal = useDisclosure()
  const isMobile = useIsMobile()
  const demoMode = useDemoMode()

  const [searchParams, setSearchParams] = useSearchParams()
  const { data: viewSettings, isLoading: isViewSettingsLoading } = useCampaignViewSettings(id)
  const { mutate: saveViewSettings, isPending: isSavingView } = useSaveCampaignViewSettings(id ?? '')
  const { data: followUpSummary } = useFollowUpSummary(id)
  // CF-1 Follow-up section actions; P4/P5 add their slot builders as further mergeSectionSlots args.
  const { data: followUpKinds } = useFollowUpKinds()
  const followUpSlots = mergeSectionSlots(
    useFollowUpRouteSlots(id ?? ''),
    // P5's chase actions lead the Chase section; P4's Chase script editor stacks below them.
    { Chase: <ChaseSection campaignId={id ?? ''} /> },
    buildCallSectionSlots(id ?? '', followUpKinds ?? []),
  )
  const followUpRowActions = buildCallRowActions(id ?? '', followUpKinds ?? [])

  const handleEdit = async (data: { label: string; description?: string; status?: string; refreshStrategyOverride?: string }) => {
    if (!campaign) return
    try {
      await updateMutation.mutateAsync({
        label: data.label,
        description: data.description,
        status: (data.status ?? campaign.status) as typeof campaign.status,
        refreshStrategyOverride: data.refreshStrategyOverride as any,
      })
      toaster.create({ title: 'Campaign updated', type: 'success', duration: 3000 })
      editModal.onClose()
    } catch {
      toaster.create({ title: 'Update failed', type: 'error', duration: 5000 })
    }
  }

  const handleRefreshLists = async () => {
    try {
      await refreshMutation.mutateAsync()
      toaster.create({ title: 'Campaign data refreshed', type: 'success', duration: 3000 })
    } catch {
      toaster.create({ title: 'Refresh failed', type: 'error', duration: 5000 })
    }
  }

  if (isLoading) {
    return (
      <Container maxW="container.xl" py={8}>
        <VStack py={12}>
          <Spinner size="lg" color="accent" />
          <Text color="gray.500">Loading campaign...</Text>
        </VStack>
      </Container>
    )
  }

  if (!!error || !campaign) {
    return (
      <Container maxW="container.xl" py={8}>
        <Alert.Root status="error" borderRadius="md">
          <Alert.Indicator />
          Failed to load campaign. It may not exist or you may not have access.
        </Alert.Root>
        <Button mt={4} onClick={() => navigate('/dashboard/campaigns')}>
          Back to Campaigns
        </Button>
      </Container>
    );
  }

  const isDraft = campaign.status === 'Draft'

  // An explicit ?tab always wins; otherwise the effective display mode picks the opening tab.
  // Without ?tab we wait for view-settings (mounting neither tab) so the page never opens on
  // Canvass and then flips; a view-settings error falls back to Standard / Canvass.
  const effectiveMode: CampaignDisplayMode = viewSettings?.effectiveMode ?? 'Standard'
  const tabParam = searchParams.get('tab')
  const hasExplicitTab = (CAMPAIGN_TABS as readonly string[]).includes(tabParam ?? '')
  const activeTab: CampaignTab = hasExplicitTab
    ? tabParam as CampaignTab
    : effectiveMode === 'FinalPush' ? 'follow-up' : 'canvass'
  const tabPending = !hasExplicitTab && isViewSettingsLoading
  const openFollowUps = followUpSummary?.kinds.reduce((sum, k) => sum + k.open, 0)
  const tabTouch = isMobile ? { minH: '44px' } : {}

  // Ruling R5: the selector shows the EFFECTIVE mode, so a date-driven Final push reads as Final
  // push. Choosing Standard clears the date (else the date would keep it in Final push).
  const handleViewChange = (displayMode: CampaignDisplayMode) => {
    if (!viewSettings || displayMode === viewSettings.effectiveMode) return
    const finalPushFrom = displayMode === 'Standard' ? null : viewSettings.finalPushFrom
    saveViewSettings({ displayMode, finalPushFrom }, {
      onError: (err) => toaster.create({
        title: extractApiMessage(err, 'Could not change the view.'), type: 'error', duration: 5000,
      }),
    })
  }

  // Extracted verbatim from the original JSX below so they can be reordered by isMobile
  // without any behavior change (see Zone 1 continued / Zone 2 below).
  const statsBlock = (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <CampaignLifetimeStats stats={stats} createdAt={campaign.createdAt} />
    </Box>
  )

  const todayBlock = (
    <Box bg="surface" p={6} borderRadius="lg" shadow="md">
      <HStack gap={3} mb={4}>
        <Icon color="accent" asChild><FiBarChart2 /></Icon>
        <Text fontWeight="medium">Today</Text>
      </HStack>
      <TodayPanel campaignId={id!} />
    </Box>
  )

  return (
    <Container maxW="container.xl" py={8}>
      <VStack gap={6} align="stretch">
        {/* Back */}
        {back && (
          <HStack>
            <BackLink to={back.to} label={back.label} />
          </HStack>
        )}

        {/* Zone 1: Header */}
        <HStack justify="space-between" align="start" flexWrap="wrap" gap={3}>
          <Box minW={0}>
            <HStack gap={3}>
              <Heading size="lg">{campaign.label}</Heading>
              <Badge colorPalette={statusBadgeColor[campaign.status]} fontSize="sm">
                {campaign.status}
              </Badge>
              {campaign.refreshStrategyOverride && (
                <Badge colorPalette="purple" fontSize="sm" variant="outline">
                  Override: {campaign.refreshStrategyOverride}
                </Badge>
              )}
            </HStack>
            {campaign.description && (
              <Text color="gray.500" mt={1}>{campaign.description}</Text>
            )}
            {!isDraft && (
              <Box mt={3}>
                <HStack gap={2} flexWrap="wrap">
                  <Text fontSize="sm" color="muted">View</Text>
                  <SegmentGroup.Root
                    value={viewSettings?.effectiveMode ?? 'Standard'}
                    onValueChange={(e) => { if (e.value) handleViewChange(e.value as CampaignDisplayMode) }}
                    disabled={!viewSettings || isSavingView}
                    colorPalette="brand"
                    size="sm"
                    css={isMobile ? { '& [data-part=item]': { minHeight: '44px' } } : undefined}
                  >
                    <SegmentGroup.Indicator />
                    <SegmentGroup.Items items={VIEW_OPTIONS} />
                  </SegmentGroup.Root>
                </HStack>
                <Text fontSize="xs" color="muted" mt={1}>Display only — the campaign doesn't change.</Text>
              </Box>
            )}
          </Box>
          <HStack gap={2}>
            {canManuallyRefreshCampaign(campaign.refreshStrategyOverride) && !isDraft && (
              <Button
                size="sm"
                variant="outline"
                colorPalette="green"
                onClick={handleRefreshLists}
                loading={refreshMutation.isPending}><FiRefreshCw />Refresh Data
                              </Button>
            )}
            <Button size="sm" variant="outline" onClick={editModal.onOpen}><FiEdit2 />Edit
                          </Button>
          </HStack>
        </HStack>

        {/* STATUS-AWARE CONTENT */}
        {isDraft ? (
          /* ============ DRAFT MODE ============ */
          (<DraftSetupView campaign={campaign} />)
        ) : (
          /* ============ ACTIVE MODE ============ */
          (<Tabs.Root
            value={tabPending ? null : activeTab}
            onValueChange={(details) => {
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev)
                next.set('tab', details.value)
                return next
              })
            }}
            colorPalette="brand" lazyMount unmountOnExit
          >
            <Tabs.List overflowX="auto" overflowY="hidden"
              css={{
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                '& > button': { flexShrink: 0 }
              }}>
              <Tabs.Trigger value="canvass" {...tabTouch}>Canvass</Tabs.Trigger>
              <Tabs.Trigger value="follow-up" {...tabTouch}>
                {openFollowUps === undefined ? 'Follow-up' : `Follow-up · ${openFollowUps}`}
              </Tabs.Trigger>
            </Tabs.List>
            {tabPending ? (
              <VStack py={8}><Spinner size="md" color="accent" /></VStack>
            ) : (
            <Tabs.ContentGroup>
            <Tabs.Content value="canvass" px={0}>
            <VStack gap={6} align="stretch">
            {/* Zone 1 continued / Zone 2: Lifetime Stats + Today Panel
                Mobile promotes TodayPanel above the lifetime stats; desktop order is unchanged. */}
            {isMobile ? <>{todayBlock}{statsBlock}</> : <>{statsBlock}{todayBlock}</>}

            {/* Zone 3: Planning */}
            <PlanningSection campaignId={id!} campaignLabel={campaign.label} readOnly={isMobile} />

            {/* Zone 4: Configuration (de-emphasized) */}
            <Box>
              <HStack gap={3} mb={4}>
                <Icon color="gray.400" asChild><FiSettings /></Icon>
                <Text fontWeight="medium" color="gray.500">Configuration</Text>
              </HStack>
              <VStack gap={4} align="stretch">
                <UnionOnly>
                  <UnionSignalsCard campaignId={id!} counts={stats?.unionSignals} />
                </UnionOnly>
                <CampaignScriptCard campaignId={id!} />
                {demoMode && <SimulationPanel campaignId={id!} />}
                <Box bg="surface" p={6} borderRadius="lg" shadow="md">
                  <Text fontWeight="medium" mb={4} color="gray.600">Campaign Tools</Text>
                  <SimpleGrid columns={{ base: 2, md: 3 }} gap={3}>
                    <ToolCard
                      icon={FiUsers}
                      label="Canvassers"
                      description="Mint access & manage roster"
                      to={`/dashboard/campaigns/${id}/canvassers`}
                    />
                    <ToolCard
                      icon={FiFileText}
                      label="Surveys"
                      description="Create survey questions"
                      to="/dashboard/canvassing/surveys"
                    />
                    <ToolCard
                      icon={FiTag}
                      label="Tags"
                      description="Manage contact tags"
                      to="/dashboard/canvassing/tags"
                    />
                    <ToolCard
                      icon={FiBookOpen}
                      label="Scripts"
                      description="Build canvassing scripts"
                      to="/dashboard/canvassing/scripts"
                    />
                  </SimpleGrid>
                </Box>
              </VStack>
            </Box>
            </VStack>
            </Tabs.Content>
            <Tabs.Content value="follow-up" px={0}>
              <FollowUpTab campaignId={id!} effectiveMode={effectiveMode} sectionSlots={followUpSlots} rowActions={followUpRowActions} />
            </Tabs.Content>
            </Tabs.ContentGroup>
            )}
          </Tabs.Root>)
        )}
      </VStack>

      {/* Edit Modal */}
      <CampaignFormModal
        isOpen={editModal.open}
        onClose={editModal.onClose}
        campaign={campaign}
        onSubmit={handleEdit}
        isLoading={updateMutation.isPending}
      />
    </Container>
  );
}
